import React, { createContext, useContext, useEffect, useState } from 'react';

import {
  fetchHomeNews as fetchHomeNewsFromFirestore,
  loadCategoryNews as loadCategoryNewsFromFirestore,
  getCategoryNewsCount,
  loadSectionNews as loadSectionNewsFromFirestore,
  getSectionNewsCount,
  searchNewsByTag as searchNewsByTagFromFirestore,
  getSearchNewsCount,
  addNewsToFirestore,
  updateNewsInFirestore,
  deleteNewsFromFirestore,
  loadPopularNews as loadPopularNewsFromFirestore,
  loadAdminNews,
  loadAllNews as loadAllNewsFromFirestore,
  getAllNewsCount,
  loadRelatedNews as loadRelatedNewsFromFirestore,
} from '../services/newsService';
import {
  getCachedNews,
  invalidateNewsCache,
  loadNewsWithCache,
  normalizeNewsCacheKeyPart,
  newsCacheKeys,
} from '../services/newsCache';
import { normalizeSearchText } from '../utils/searchText';

const NewsContext = createContext();

export const useNews = () => useContext(NewsContext);

const fetchHomeNews = (limitCount = 80) =>
  loadNewsWithCache(
    newsCacheKeys.home(limitCount),
    () => fetchHomeNewsFromFirestore(limitCount)
  );

const loadCategoryNews = (category, limitCount = 30) => {
  if (!normalizeNewsCacheKeyPart(category)) {
    return Promise.resolve([]);
  }

  return loadNewsWithCache(
    newsCacheKeys.category(category, limitCount),
    () => loadCategoryNewsFromFirestore(category, limitCount)
  );
};

const loadSectionNews = (section, limitCount = 24) => {
  if (!normalizeNewsCacheKeyPart(section)) {
    return Promise.resolve([]);
  }

  const cacheKey = newsCacheKeys.section(section, limitCount);

  if (process.env.NODE_ENV === 'development') {
    const cacheEvent = getCachedNews(cacheKey) === undefined
      ? 'CLUB NEWS REQUEST START'
      : 'CLUB NEWS CACHE HIT';
    console.debug(
      `[${cacheEvent}] ${section} ${performance.now().toFixed(1)}ms`
    );
  }

  return loadNewsWithCache(cacheKey, async () => {
    const requestStart = process.env.NODE_ENV === 'development'
      ? performance.now()
      : 0;
    const data = await loadSectionNewsFromFirestore(section, limitCount);

    if (process.env.NODE_ENV === 'development') {
      const requestEnd = performance.now();
      console.debug(
        `[CLUB NEWS FIRESTORE COMPLETE] ${section} `
        + `${requestEnd.toFixed(1)}ms duration=${(requestEnd - requestStart).toFixed(1)}ms`
      );
    }

    return data;
  });
};

const searchNewsByTag = (tag, limitCount = 30) => {
  const normalizedTag = normalizeSearchText(tag);
  if (!normalizedTag) {
    return Promise.resolve([]);
  }

  return loadNewsWithCache(
    newsCacheKeys.search(normalizedTag, limitCount),
    () => searchNewsByTagFromFirestore(normalizedTag, limitCount)
  );
};

const loadPopularNews = (limitCount = 20) =>
  loadNewsWithCache(
    newsCacheKeys.popular(limitCount),
    () => loadPopularNewsFromFirestore(limitCount)
  );

const loadAllNews = (limitCount = 30) =>
  loadNewsWithCache(
    newsCacheKeys.all(limitCount),
    () => loadAllNewsFromFirestore(limitCount)
  );

const loadRelatedNews = (currentNews, limitCount = 12) => {
  const hasSection = Array.isArray(currentNews?.sections)
    && normalizeNewsCacheKeyPart(currentNews.sections[0]);
  const hasCategory = normalizeNewsCacheKeyPart(currentNews?.category);

  if (!currentNews?.id || (!hasSection && !hasCategory)) {
    return Promise.resolve([]);
  }

  return loadNewsWithCache(
    newsCacheKeys.related(currentNews, limitCount),
    () => loadRelatedNewsFromFirestore(currentNews, limitCount)
  );
};

export const NewsProvider = ({ children }) => {
  const [newsList, setNewsList] = useState([]);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [newsError, setNewsError] = useState(null);

  useEffect(() => {
    let alive = true;

    const fetchNews = async () => {
      try {
        setNewsError(null);

        const data = await fetchHomeNews(80);

        if (alive) {
          setNewsList(data);
        }
      } catch (err) {
        console.error('Error loading news:', err);

        if (alive) {
          setNewsError(err);
        }
      } finally {
        if (alive) {
          setFirstLoadDone(true);
        }
      }
    };

    fetchNews();

    return () => {
      alive = false;
    };
  }, []);

  const addNews = async (newNews) => {
    try {
      const optimistic = await addNewsToFirestore(newNews);

      invalidateNewsCache();
      setNewsList((prev) => [optimistic, ...prev]);
    } catch (error) {
      console.error('Error adding news:', error);
      throw error;
    }
  };

  const editNews = async (updatedNews) => {
    try {
      const savedNews = await updateNewsInFirestore(updatedNews);

      invalidateNewsCache();
      setNewsList((prev) =>
        prev.map((n) => (n.id === savedNews.id ? savedNews : n))
      );
    } catch (error) {
      console.error('Error updating news:', error);
      throw error;
    }
  };

  const deleteNews = async (id) => {
    try {
      await deleteNewsFromFirestore(id);

      invalidateNewsCache();
      setNewsList((prev) => prev.filter((n) => n.id !== id));
    } catch (error) {
      console.error('Error deleting news:', error);
      throw error;
    }
  };

  return (
    <NewsContext.Provider
      value={{
        newsList,
        firstLoadDone,
        newsError,

        // Головна
        fetchHomeNews,

        // Категорії
        loadCategoryNews,
        getCategoryNewsCount,

        // Розділи
        loadSectionNews,
        getSectionNewsCount,

        // Пошук
        searchNewsByTag,
        getSearchNewsCount,

        // CRUD
        addNews,
        editNews,
        deleteNews,

        loadPopularNews,
        loadAdminNews,
        loadAllNews,
getAllNewsCount,
loadRelatedNews,
      }}
    >
      {children}
    </NewsContext.Provider>
  );
};
